import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useScrollAnimation } from '../../../shared/hooks'
import SectionHeader from '../../../shared/components/ui/SectionHeader'
import ProjectCard from '../../../components/cards/ProjectCard'
import { API_BASE } from '../../../config/api'

interface CompanyProject {
  id: string
  title?: string
  description?: string
  image?: string
  status?: string
  technologies?: string[]
  features?: string[]
  duration?: string
  team?: string
}

export default function CompanyProjectsSection() {
  const { ref, isInView } = useScrollAnimation()
  const [projects, setProjects] = useState<CompanyProject[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/api/v1/projects?type=business&limit=4`)
        if (!response.ok) {
          throw new Error(`Projects API request failed: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        const list: unknown[] = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        const normalized = list.slice(0, 4).map((raw, index) => normalizeProject(raw, index))
        setProjects(normalized)

        if (!Array.isArray(list) || (data?.success === false && data?.error)) {
          setError(typeof data?.error === 'string' ? data.error : 'Unexpected API response')
        }
      } catch (err) {
        console.error('Failed to fetch company projects:', err)
        setProjects([])
        setError(err instanceof Error ? err.message : 'Failed to fetch projects')
      } finally {
        setIsLoading(false)
      }
    }

    fetchProjects()
  }, [])

  return (
    <section id="company-projects" ref={ref} className="section-spacing">
      <div className="container-page">
        <SectionHeader
          badge="Our Professional Work"
          title="Company Projects"
          description="Explore our portfolio of professional client work and business solutions that have transformed organizations and created lasting impact."
          isInView={isInView}
        />

        {error && (
          <div className="text-center text-sm mb-8" style={{ color: 'var(--muted)' }}>
            {error}
          </div>
        )}

        <ProjectsGrid projects={projects} isLoading={isLoading} />
        <CallToAction />
      </div>
    </section>
  )
}

function normalizeProject(raw: unknown, fallbackIndex: number): CompanyProject {
  const obj = (raw ?? {}) as Record<string, unknown>
  const id = typeof obj.id === 'string' && obj.id ? obj.id : `business-${fallbackIndex}`
  const technologies = Array.isArray(obj.technologies)
    ? obj.technologies.filter((item): item is string => typeof item === 'string')
    : []
  const features = Array.isArray(obj.features)
    ? obj.features.filter((item): item is string => typeof item === 'string')
    : []

  return {
    id,
    title: typeof obj.title === 'string' ? obj.title : undefined,
    description: typeof obj.description === 'string' ? obj.description : undefined,
    image: typeof obj.image === 'string' ? obj.image : undefined,
    status: typeof obj.status === 'string' ? obj.status : undefined,
    technologies,
    features,
    duration: typeof obj.duration === 'string' ? obj.duration : undefined,
    team: typeof obj.team === 'string' ? obj.team : undefined,
  }
}

function ProjectsGrid({ projects, isLoading }: { projects: CompanyProject[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {[1, 2, 3, 4].map((placeholder) => (
          <div key={placeholder} className="shimmer-card" style={{ height: '480px' }}>
            <div>
              {/* Header skeleton */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-lg shimmer-item flex-shrink-0" />
                <div className="flex-1 space-y-2 mt-1">
                  <div className="h-4 w-3/4 rounded shimmer-item" />
                  <div className="h-3 w-1/3 rounded shimmer-item" />
                </div>
              </div>
              {/* Description skeleton */}
              <div className="space-y-2 mb-6">
                <div className="h-3 w-full rounded shimmer-item" />
                <div className="h-3 w-full rounded shimmer-item" />
                <div className="h-3 w-2/3 rounded shimmer-item" />
              </div>
              {/* Tags skeleton */}
              <div className="flex gap-2 mb-6">
                <div className="h-6 w-16 rounded-full shimmer-item" />
                <div className="h-6 w-20 rounded-full shimmer-item" />
              </div>
            </div>
            {/* Footer skeleton */}
            <div className="flex justify-between items-center pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <div className="h-3 w-24 rounded shimmer-item" />
              <div className="h-3 w-16 rounded shimmer-item" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!projects.length) {
    return (
      <div className="text-center mb-12" style={{ color: 'var(--muted)' }}>
        No projects available right now. Check back soon!
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
      {projects.map((project) => (
        <div key={project.id}>
          <ProjectCard project={project} />
        </div>
      ))}
    </div>
  )
}

function CallToAction() {
  return (
    <div className="text-center mt-12">
      <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
        Want to see more of our professional work?
      </p>
      <Link to="/projects" className="btn-pill btn-ghost no-underline">
        View All Projects
      </Link>
    </div>
  )
}